import { usePrivy } from '@privy-io/react-auth';
import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { PrivyAuthProvider } from './components/auth/PrivyProvider';
import { Holdings } from './components/dashboard/Holdings';
import { UserWallet } from './types';
import { callFunction } from './lib/firebase-functions';
import { WalletAddress } from './components/dashboard/WalletAddress';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { History } from './components/dashboard/History';
import { FAQ } from './components/pages/FAQ';
import { Terms } from './components/pages/Terms';
import { Footer } from './components/layout/Footer';
import { Transition } from '@headlessui/react';

// Add cache helper functions
const CACHE_KEY = 'ditto_wallet_cache';

interface CachedData {
  data: UserWallet;
  timestamp: number;  // Keep timestamp for debugging/future use
}

const getCachedWallets = (userId: string): UserWallet | null => {
  try {
    const cached = localStorage.getItem(`${CACHE_KEY}_${userId}`);
    if (!cached) return null;
    const { data }: CachedData = JSON.parse(cached);
    return data;
  } catch (error) {
    console.error('Error reading cache:', error);
    return null;
  }
};

const setCachedWallets = (userId: string, data: UserWallet) => {
  try {
    const cacheData: CachedData = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(`${CACHE_KEY}_${userId}`, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error setting cache:', error);
  }
};

// Update the LoadingBar component
const LoadingBar = ({ isComplete }: { isComplete: boolean }) => (
  <div className="w-full">
    <div className="relative h-0.5 w-full overflow-hidden bg-gray-200">
      <div 
        className={`absolute h-full bg-green-500 transition-all
          ${isComplete 
            ? 'w-full duration-200' 
            : 'animate-[growWidth_15s_ease-out_forwards]'  // 15s total: ~5s to 50%, ~10s to 90%
          }`}
      />
    </div>
  </div>
);

function MainContent() {
  const { login, ready, authenticated, user, getAccessToken, logout } = usePrivy();
  const [wallets, setWallets] = useState<UserWallet | null>(null);
  const [activeTab, setActiveTab] = useState<'holdings' | 'history'>('holdings');
  const [isFirstLoading, setIsFirstLoading] = useState(false);

  useEffect(() => {
    async function fetchWallets(periodicFetch: boolean = false) {
      if (!user?.id || !authenticated) return;
      
      if (!periodicFetch) {
        if (isFirstLoading) return;
        
        // Try to load from cache first
        const cached = getCachedWallets(user.id);
        if (cached) {
          setWallets(cached);
        }
        
        // Show loading bar for first fetch, even with cache
        setIsFirstLoading(true);
      }
      
      try {
        const token = await getAccessToken();
        if (!token) return;
        
        const data = await callFunction('getWallets', {}, token);
        setWallets(data);
        setCachedWallets(user.id, data);
      } catch (error) {
        console.error('Error fetching wallets:', error);
        if (!periodicFetch && !getCachedWallets(user.id)) {
          setWallets(null);
        }
      } finally {
        if (!periodicFetch) {
          setIsFirstLoading(false);
        }
      }
    }

    // Initial fetch
    fetchWallets(false);

    // Create a named function for the periodic fetch
    const periodicFetch = () => fetchWallets(true);

    // Set up interval for subsequent fetches using function reference
    const intervalId = setInterval(periodicFetch, 10000);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [user?.id, authenticated, getAccessToken]);

  if (!ready) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-gray-900">Loading...</div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center mb-8">
          <img 
            src="/ditto-logo.png"
            alt="Ditto Wallet Bot"
            className="w-24 h-24 mb-6 mx-auto"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-1">
            ditto
          </h1>
          <div className="text-gray-600 mb-6">@dittowalletbot</div>
          <p className="text-gray-600 text-lg mb-6 max-w-md">
            Tag <a 
              href="https://x.com/dittowalletbot" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-600 hover:text-blue-700 transition-colors"
            >@dittowalletbot</a> in any X post to use your pre-generated Base & Solana wallet.
          </p>
        </div>
        <button 
          onClick={() => login()} 
          className="px-6 py-3 bg-white text-gray-900 font-bold rounded-full hover:bg-gray-50 border border-gray-300 transition-colors"
        >
          View your wallet
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
      <div className="flex-grow">
        <div className="max-w-4xl mx-auto pt-8 pb-8">
          <div className="border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {user?.twitter?.profilePictureUrl && (
                  <img 
                    src={user.twitter.profilePictureUrl} 
                    alt="Profile" 
                    className="w-12 h-12 rounded-full"
                  />
                )}
                <div>
                  <div className="font-bold text-lg">{user?.twitter?.name}</div>
                  <div className="text-gray-600">@{user?.twitter?.username}</div>
                </div>
              </div>
              <button
                onClick={logout}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-full text-sm transition-colors"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Wallet Addresses */}
          {wallets === null ? (
            <div className="p-4">
              {/* <div className="flex items-center justify-center gap-2 text-gray-600">
                <ArrowPathIcon className="w-5 h-5 animate-spin" />
                <span>Loading your wallet addresses...</span>
              </div> */}
            </div>
          ) : (
            <div className="border-gray-200 p-4">
              <div className="space-y-2">
                {wallets.ethereumAddress && (
                  <WalletAddress label="Base" address={wallets.ethereumAddress} />
                )}
                {wallets.solanaAddress && (
                  <WalletAddress label="Solana" address={wallets.solanaAddress} />
                )}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-gray-200 mb-4">
            <button
              onClick={() => setActiveTab('holdings')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'holdings' 
                  ? 'text-gray-900 border-b-2 border-blue-500' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Tokens {wallets?.holdings && `($${(
                (wallets.holdings.ethereum?.reduce((sum, token) => sum + (token.usd_value || 0), 0) || 0) +
                (wallets.holdings.solana?.reduce((sum, token) => sum + (token.usd_value || 0), 0) || 0)
              ).toFixed(2)})`}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'history' 
                  ? 'text-gray-900 border-b-2 border-blue-500' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Activities
            </button>
          </div>

          <Transition
            show={isFirstLoading}
            enter="transition-opacity duration-75"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Transition.Child as="div">
              <LoadingBar isComplete={!isFirstLoading} />
            </Transition.Child>
          </Transition>

          {/* Tab Content */}
          {wallets === null ? (
            <div className="bg-white shadow sm:rounded-lg p-8">
              <div className="flex items-center justify-center gap-2 text-gray-600">
                <ArrowPathIcon className="w-5 h-5 animate-spin" />
                <span>Loading your wallets...</span>
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'holdings' && wallets?.holdings && (
                <Holdings 
                  ethereum={wallets.holdings.ethereum} 
                  solana={wallets.holdings.solana} 
                />
              )}
              {activeTab === 'history' && wallets?.transactions && (
                <History 
                  transactions={wallets.transactions}
                  twitterUsername={user?.twitter?.username || undefined}
                  userSolanaAddress={wallets.solanaAddress || ''}
                  profileImageUrl={user?.twitter?.profilePictureUrl ?? undefined}
                />
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <PrivyAuthProvider>
        <Routes>
          <Route path="/" element={<MainContent />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </PrivyAuthProvider>
    </Router>
  );
} 